import {
  type BrandColors,
  getCalculatedStyleProperties,
  hexToHSL,
  type HSLString,
  hslToHex,
  setGlobalStyleProperties,
  TURNIT_BRAND_COLORS,
} from '@packages/design-tokens';

import type { SalesPointConfigurationResponseDTO } from '@/types/dto/agent';
import type { TenantOption } from '@/types/tenant';

// Re-export the design-tokens colour helpers the rest of the engine consumes,
// so there is a single import surface and no duplicated colour maths.
export { getCalculatedStyleProperties, hslToHex };

// design-tokens does not emit a `white` token, so keep it as a local constant.
export const WHITE = '0, 0%, 100%' as HSLString;

export const decodeBase64SVG = (
  base64: string,
  background: HSLString,
  outline: HSLString
) => {
  const decoded = atob(base64);

  let count = 0;

  const updatedSvgString = decoded.replace(/fill="[^"]*"/g, () => {
    let newFill;

    if (count === 0) {
      newFill = 'fill="none"';
    } else if (count === 1) {
      newFill = `fill="${hslToHex(background)}"`;
    } else {
      newFill = `fill="${hslToHex(outline)}"`;
    }

    count++;
    return newFill;
  });

  return btoa(updatedSvgString);
};

// Keeps only the colour fields the API actually populated, dropping any
// `null`/`undefined` value so a partially-configured organization never
// clobbers a valid Turnit default when the two are merged. Typed as a
// `Partial<BrandColors>` because the API's `generalColors` field names are a
// subset of `BrandColors`; the runtime object may also carry fields the
// generated swagger schema does not yet declare (e.g. `cta`, `ctaContrast`,
// `background`, `backgroundText`), and those flow through the spread untouched.
const definedBrandColors = (
  colors: Partial<BrandColors> | null | undefined
): Partial<BrandColors> =>
  Object.fromEntries(
    Object.entries(colors ?? {}).filter(([, value]) => value != null)
  ) as Partial<BrandColors>;

// Maps the API `organizationBranding` (a field on the sales-point configuration)
// to the design-tokens `BrandColors` shape by merging it over the Turnit
// default: every colour the API provides wins, and any field it omits keeps the
// Turnit value. The default is a complete `BrandColors`, so the merged object
// always carries the eight fields `getCalculatedStyleProperties` requires —
// independent of how many fields the API (or its swagger schema) currently
// ships. This lets new brand tokens (`cta`, `ctaContrast`, …) light up per-field
// as the backend starts returning them, with no further UI change.
export const mapOrganizationBrandingToBrandColors = (
  branding:
    | SalesPointConfigurationResponseDTO['organizationBranding']
    | undefined,
  tenant: TenantOption['value']
): BrandColors => {
  const isMaas = tenant === 'maas';
  const isTifrance = tenant === 'tifrance';

  // MaaS instance configuration GET request currently does not return organizationBranding field with it.
  // hardcoding values is a temporary solution and should be changed later once the above is fixed
  if (isMaas) {
    return {
      primary: '#005aa3',
      dark: '#151926',
      tint: '#e77400',
      accent: '#994d00',
      cta: '#FFD370',
      ctaContrast: '#151926',
      background: '#003662',
      backgroundText: '#FFFFFF',
    };
  }

  // TIFrance instance configuration GET request currently does not return organizationBranding field with it.
  // hardcoding values is a temporary solution and should be changed later once the above is fixed
  if (isTifrance) {
    return {
      primary: '#03665E',
      accent: '#12a4a5',
      dark: '#212529',
      tint: '#212529',
      cta: '#d91835',
      ctaContrast: '#ffffff',
      background: '#ffffff',
      backgroundText: '#212529',
    };
  }

  return {
    ...TURNIT_BRAND_COLORS,
    ...definedBrandColors(branding?.colors?.generalColors),
  };
};

// True when content drawn on `hex` needs the light-on-dark treatment. Uses the
// same HSL lightness the token palette is derived from, so the answer agrees
// with the calculated colour variants.
export const isDarkBrandColor = (hex: string): boolean => {
  const [, , lightness] = hexToHSL(hex).split(',');

  return Number.parseFloat(lightness) < 50;
};

export const setTenantBrand = (brandColors: BrandColors) => {
  const calculatedStyleProperties = getCalculatedStyleProperties(brandColors);

  // `background` and `background-text` are brand-defined tokens emitted
  // directly by design-tokens (no inference) — `setGlobalStyleProperties`
  // writes them straight to `--background` / `--background-text`.
  setGlobalStyleProperties(calculatedStyleProperties);

  // Page content uses the brand on-background text colour by default.
  document.body.classList.remove('text-white', 'text-dark');
  document.body.classList.add('text-background-text');
};

// Sets the favicon from the API branding `faviconUrl`, defaulting to the Turnit
// favicon when the organization has none.
export const setTenantFavicon = (faviconUrl?: string) => {
  const favicon = document.head.querySelector<HTMLLinkElement>(
    'link#tenant-favicon'
  );

  if (favicon) {
    favicon.href = faviconUrl || '/turnit.png';
  }
};
